import { useEffect, useState } from "react";

import { LINE_OPTION_OVERLAY, menuBlockFromElement } from "./lineMenuBlocks";

import type { Editor } from "@tiptap/core";
import type { Transaction } from "@tiptap/pm/state";
import type { Dispatch, PointerEvent as ReactPointerEvent, SetStateAction } from "react";

export type HoveredMenuBlock = {
  pos: number;
  /** The pointer is on the line's collapse toggle, which shows its own hover state. */
  isOverToggle: boolean;
};

/**
 * Moves the hovered block to the one under `element`. Without a block there, it is cleared
 * unless `isKept`.
 */
function hoverAt(
  setHovered: Dispatch<SetStateAction<HoveredMenuBlock | null>>,
  editor: Editor,
  element: Element | null,
  isKept: boolean,
) {
  // The overlay covers the pointer, so the line beneath it is still the hovered one.
  if (element?.closest(LINE_OPTION_OVERLAY)) {
    return;
  }

  const pos = element ? menuBlockFromElement(editor, element) : null;

  if (pos === null) {
    if (!isKept) {
      setHovered(null);
    }
    return;
  }

  const isOverToggle = !!element?.closest("[data-collapse-toggle]");

  setHovered((previous) =>
    previous?.pos === pos && previous.isOverToggle === isOverToggle
      ? previous
      : { pos, isOverToggle },
  );
}

/**
 * The block with a menu under the pointer. It stays while the pointer is on the overlay or
 * `isMenuOpen`, since the overlay is portaled outside the line.
 */
export function useHoveredMenuBlock(
  editor: Editor | undefined,
  root: HTMLElement | null,
  isMenuOpen: boolean,
) {
  const [hovered, setHovered] = useState<HoveredMenuBlock | null>(null);
  const [isOverOverlay, setIsOverOverlay] = useState(false);
  const isKept = isOverOverlay || isMenuOpen;

  useEffect(() => {
    if (!editor || !root) {
      return;
    }

    let lastPointer: { x: number; y: number } | null = null;

    const onPointerMove = (event: PointerEvent) => {
      lastPointer = { x: event.clientX, y: event.clientY };
      hoverAt(setHovered, editor, event.target instanceof Element ? event.target : null, isKept);
    };

    const onPointerLeave = (event: PointerEvent) => {
      const next = event.relatedTarget;

      if (!(next instanceof Element && next.closest(LINE_OPTION_OVERLAY))) {
        hoverAt(setHovered, editor, null, isKept);
      }
    };

    // Scrolling moves the lines under a pointer that stays put.
    const onScroll = () => {
      if (lastPointer) {
        const element = document.elementFromPoint(lastPointer.x, lastPointer.y);
        hoverAt(setHovered, editor, element, isKept);
      }
    };

    // Scrolling over the overlay's buttons would leave them stuck where the pointer no longer is.
    const onWheel = (event: WheelEvent) => {
      const { deltaX, deltaY } = event;

      if (isOverOverlay && Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) >= 1) {
        setIsOverOverlay(false);
        hoverAt(setHovered, editor, null, isMenuOpen);
      }
    };

    // The hovered block keeps its place through edits before it, and goes with its deletion.
    const onTransaction = ({ transaction }: { transaction: Transaction }) => {
      if (!transaction.docChanged) {
        return;
      }

      setHovered((previous) => {
        if (!previous) {
          return previous;
        }

        const { pos, deleted } = transaction.mapping.mapResult(previous.pos);

        if (deleted) {
          return null;
        }

        return pos === previous.pos ? previous : { ...previous, pos };
      });
    };

    root.addEventListener("pointermove", onPointerMove, { passive: true });
    root.addEventListener("pointerleave", onPointerLeave, { passive: true });
    root.addEventListener("wheel", onWheel, { passive: true, capture: true });
    window.addEventListener("scroll", onScroll, true);
    editor.on("transaction", onTransaction);

    return () => {
      root.removeEventListener("pointermove", onPointerMove);
      root.removeEventListener("pointerleave", onPointerLeave);
      root.removeEventListener("wheel", onWheel, true);
      window.removeEventListener("scroll", onScroll, true);
      editor.off("transaction", onTransaction);
    };
  }, [editor, root, isKept, isOverOverlay, isMenuOpen]);

  return {
    hovered,
    isOverOverlay,
    /** Forgets the hovered block, as when its menu closes away from the overlay. */
    clear: () => setHovered(null),
    overlayPointerHandlers: {
      onPointerEnter: () => setIsOverOverlay(true),
      onPointerLeave: (event: ReactPointerEvent) => {
        setIsOverOverlay(false);

        if (editor) {
          const element = document.elementFromPoint(event.clientX, event.clientY);
          hoverAt(setHovered, editor, element, isMenuOpen);
        }
      },
    },
  };
}
